import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { FormControl } from "@material-ui/core";
import moment from "moment";

import { formatTagsData, getChangedData, getDefaultWidgetName, getFilteredTag, getTagid } from "utils/utils";
import { InputErrorStrings, TimeSeriesWidgetFormStrings, valueStrings, WidgetStrings } from "constants/constantStrings";
import { DATE_FORMAT, WIDGET_TITLE_INPUT_MAXIMUM } from "constants/globalConstants";
import { SYSTEMS_PAGES } from "constants/pages.constant";
import { WIDGET_TYPES } from "constants/widgets.type.const";

import BadgeLabel from "components/BadgeLabel";
import CustomModal from "components/CustomModal";
import InputBox from "components/InputBox";
import { TextFieldValidator } from "components/InputBox/TextFieldValidator";
import TagDropdown from "components/TagDropdown";

export default function TimeseriesWidgetForm(props) {
  const { Header, page, currentLayout, isEdit, onWidgetAction, Attributes } = props.data;
  const isReportView = () => page?.toLowerCase() === SYSTEMS_PAGES.REPORTS;
  const calculatePastDate = months => moment().subtract(months?.split(" ")[0], "months").format(DATE_FORMAT);
  const { tagsAsObj } = useSelector(state => state.tags);
  const Tags = Attributes ? Attributes.SystemTag.map(tag => tagsAsObj[tag]) : null;

  const getDefaultTags = () => formatTagsData(Tags);

  const widgetDefaultState = {
    Header: Header || getDefaultWidgetName(),
    Attributes: {
      Duration: Attributes?.Duration || "1 month",
      Comments: Attributes?.Comments || "",
      SystemTag: { ...getDefaultTags() }
    }
  };

  const [openModal, setOpenModal] = useState(true);
  const [isFormInvalid, setIsFormInvalid] = useState(true);
  const [widgetData, setWidgetData] = useState(widgetDefaultState);
  const [pastDate, setPastDate] = useState(calculatePastDate(widgetDefaultState.Attributes.Duration));
  const [tagsList, setTagsList] = useState(props.data.tagsList);

  useEffect(() => setWidgetData(widgetDefaultState), [Header]);

  const levelTypesConfig = [
    { label: TimeSeriesWidgetFormStrings.past1Month, value: "1 month" },
    { label: TimeSeriesWidgetFormStrings.past3Months, value: "3 month" },
    { label: TimeSeriesWidgetFormStrings.past6Months, value: "6 month" }
  ];

  const getTagsConfig = () => {
    const noOfTags = isReportView() ? 3 : 1;
    const tagsList = new Array(noOfTags).fill("Tag");
    return tagsList.map((tag, idx) => ({ id: `Tag${idx + 1}`, value: null, required: idx === 0 }));
  };
  const currentDate = moment().format(DATE_FORMAT);

  const closeModal = closeType => {
    setOpenModal(false);
    props.onClose(closeType);
  };

  const onTitleChange = Header => {
    const formData = { ...widgetData, Header };
    validateForm(formData);
  };

  const onTagChange = (idx, value) => {
    const formData = {
      ...widgetData,
      Attributes: { ...widgetData.Attributes, SystemTag: { ...widgetData.Attributes.SystemTag, [`Tag${idx + 1}`]: value } }
    };
    validateForm(formData);
    const filteredTagsList = getFilteredTag(props.data.tagsList, formData?.Attributes?.SystemTag);
    setTagsList(filteredTagsList);
  };

  const onDateRangeChange = event => {
    const Duration = event?.target?.value;
    const formData = { ...widgetData, Attributes: { ...widgetData.Attributes, Duration } };
    setPastDate(calculatePastDate(Duration));
    validateForm(formData);
  };

  const validateForm = formData => {
    const { Header, Attributes } = formData;
    const { Duration, SystemTag } = Attributes;
    const { Tag1 } = SystemTag;
    let isFormValid = Tag1?.TagId;
    if (!isReportView()) {
      isFormValid = isFormValid && Duration;
    } else {
      isFormValid = isFormValid && Header && Header.trim().length > 0;
    }
    setIsFormInvalid(!isFormValid);
    setWidgetData(formData);
  };

  const getPayload = () => {
    const { Header, Attributes } = widgetData;
    const { Duration, Comments, SystemTag } = Attributes;
    let param = {
      Type: WIDGET_TYPES.TIMESERIES,
      LayoutID: currentLayout.ID,
      Header,
      Attributes: { Duration, Comments: Comments || "" }
    };
    param = getTagid(SystemTag, param);
    return param;
  };
  const getEditTagPaylod = () => {
    let param = getChangedData(widgetData, widgetDefaultState);
    if (param.Attributes?.SystemTag) param = getTagid(widgetData.Attributes.SystemTag, param);
    return { ...param, Id: props.data.Id, LayoutID: currentLayout.ID };
  };

  const addOnReport = async () => {
    let payload = {};
    if (isEdit) {
      payload = getEditTagPaylod();
    } else {
      payload = getPayload();
      onWidgetAction("ADD_WIDGET", payload);
    }
    closeModal(payload);
  };

  const addOnDashboard = async () => {
    let payload = {};
    if (isEdit) {
      payload = getEditTagPaylod();
    } else {
      payload = getPayload();
      onWidgetAction("ADD_WIDGET", null, null, payload);
    }
    closeModal(payload);
    setIsFormInvalid(false);
  };

  const onFormSubmit = async () => {
    setIsFormInvalid(true);
    if (isReportView()) {
      addOnReport();
    } else {
      addOnDashboard();
    }
  };

  const getDescription = () => (isReportView() ? TimeSeriesWidgetFormStrings.reportDescription : TimeSeriesWidgetFormStrings.dashboardDescription);

  return (
    <>
      {openModal && (
        <CustomModal
          show={openModal}
          title={isEdit ? TimeSeriesWidgetFormStrings.editTitle : TimeSeriesWidgetFormStrings.addTitle}
          confirmBtnText={isEdit ? WidgetStrings.editWidget : WidgetStrings.addWidget}
          description={getDescription()}
          secondaryDescription={valueStrings.mandatoryField}
          close={closeModal}
          confirmBtnAction={onFormSubmit}
          confirmDisabled={isFormInvalid}
        >
          <div className="widget-form-container">
            {isReportView() && (
              <div className="form-group">
                <label className="bodyMainHeadingText" htmlFor="widgetTitle">
                  {WidgetStrings.widgetTitle}*
                </label>
                <InputBox
                  name="widgetTitle"
                  value={widgetData.Header}
                  placeholder={WidgetStrings.titlePlaceholder}
                  requiredMsg={InputErrorStrings.required}
                  onChange={onTitleChange}
                  validator={TextFieldValidator.ALPHANUMERIC_WITH_SPECIAL_CHARS}
                  maxLength={WIDGET_TITLE_INPUT_MAXIMUM}
                />
              </div>
            )}
            {getTagsConfig().map((config, idx) => (
              <div className="form-group" key={idx}>
                <label className="bodyMainHeadingText">
                  {WidgetStrings.selectTag}
                  {config.required && <span>*</span>}
                </label>
                <TagDropdown
                  tagValue={widgetData.Attributes.SystemTag[`Tag${idx + 1}`]}
                  dropDownID={config.id}
                  tagList={tagsList}
                  requiredMsg={config.required && InputErrorStrings.required}
                  onTagChange={(value, reason) => onTagChange(idx, value, reason)}
                />
              </div>
            ))}
            {!isReportView() && (
              <FormControl>
                <label className="bodyMainHeadingText">{TimeSeriesWidgetFormStrings.dateRange}*</label>
                <BadgeLabel customClass="mb-15" badgeText={`${pastDate} - ${currentDate}`} />
                <div className="radio-group" onChange={onDateRangeChange}>
                  {levelTypesConfig.map((level, index) => (
                    <label className="radiocontainer" key={index}>
                      {level.label}
                      <input
                        className="radioLabel"
                        type="radio"
                        checked={widgetData.Attributes.Duration === level.value}
                        onChange={() => {}}
                        value={level.value}
                        name="levelType"
                      />
                      <span className="radioButton" />
                    </label>
                  ))}
                </div>
              </FormControl>
            )}
          </div>
        </CustomModal>
      )}
    </>
  );
}
